import { formatTransactionTableDate } from 'i18n/calendarLabels';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PwaChip, PwaEmpty } from 'pwa/components/ui';
import { DataJournal } from 'store/vat/models/vatReportData';

export interface PwaVatTransactionsCardListProps {
  rows: DataJournal[];
  loading?: boolean;
  emptyMessage: string;
  /** Richer empty state (Exceptions tab). */
  emptyTitle?: string;
  emptyDescription?: string;
  formatAmount: (value?: number) => string;
  onOpenDetails: (record: DataJournal) => void;
}

function getPrimaryLabel(record: DataJournal): string {
  const contact = String(record.contact ?? '').trim();
  const description = String(record.description ?? '').trim();
  const invoice = String(record.invoiceNumber ?? '').trim();
  return contact || description || invoice || '—';
}

function getSecondaryLabel(record: DataJournal): string | null {
  const contact = String(record.contact ?? '').trim();
  const description = String(record.description ?? '').trim();
  if (contact && description && contact !== description) {
    return description;
  }
  const account = String(record.accountName ?? '').trim();
  const reference = String(record.reference ?? '').trim();
  return account || reference || null;
}

function getActionLabel(record: DataJournal): string {
  return String(
    record.exceptionActionType || record.selectedValue || ''
  ).trim();
}

function actionChipTone(
  action: string
): 'ok' | 'warn' | 'danger' | 'brand' | undefined {
  const a = action.toLowerCase();
  if (!a) return undefined;
  if (a.includes('defer') || a.includes('unresolved')) return 'warn';
  if (a.includes('includ')) return 'ok';
  if (a.includes('adjust')) return 'brand';
  if (a.includes('ignore') || a.includes('out_of')) return undefined;
  return 'brand';
}

export const PwaVatTransactionsCardList: React.FC<
  PwaVatTransactionsCardListProps
> = ({
  rows,
  loading = false,
  emptyMessage,
  emptyTitle,
  emptyDescription,
  formatAmount,
  onOpenDetails,
}) => {
  const { t, i18n } = useTranslation();

  if (!loading && rows.length === 0) {
    if (emptyTitle) {
      return (
        <PwaEmpty title={emptyTitle} icon="check">
          {emptyDescription || emptyMessage}
        </PwaEmpty>
      );
    }
    return (
      <div className="scope-pwa-vat-tx-list__empty">{emptyMessage}</div>
    );
  }

  return (
    <div className="pwa-list scope-pwa-vat-tx-list" role="list">
      {rows.map((record, index) => {
        const rowKey =
          record.uuid ||
          record.sourceId ||
          record.invoiceId ||
          String(index);
        const secondary = getSecondaryLabel(record);
        const dateLabel = formatTransactionTableDate(
          record.journalDate,
          i18n.language
        );
        const action = getActionLabel(record);
        const net = record.netAmountConverted || 0;
        const total = record.grossAmountConverted || 0;
        const netLabel = formatAmount(net);
        const totalLabel = formatAmount(total);

        return (
          <button
            key={rowKey}
            type="button"
            className="pwa-row"
            role="listitem"
            onClick={() => onOpenDetails(record)}
          >
            <span className="pwa-row-body">
              <span className="pwa-row-title">{getPrimaryLabel(record)}</span>
              {secondary ? (
                <span className="pwa-row-sub wrap">{secondary}</span>
              ) : null}
              <span className="pwa-row-status">
                {action ? (
                  <PwaChip tone={actionChipTone(action)}>{action}</PwaChip>
                ) : null}
                {dateLabel ? (
                  <span className="pwa-row-meta">{dateLabel}</span>
                ) : null}
              </span>
            </span>
            <span className="pwa-row-end scope-pwa-vat-tx-row__end">
              <span
                className={`pwa-amount${total < 0 ? ' pwa-neg' : ''}`}
              >
                {totalLabel}
              </span>
              <span className="pwa-row-meta pwa-num">
                {t('transactions.net')} {netLabel}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
};
